import { useIntegrationsStore } from '@/stores/integrationsStore';
import { toFrontendKey } from '@/constants/platforms';

/** Max background refetches while waiting for the backend to persist a connection. */
export const REFRESH_MAX_ATTEMPTS = 6;
/** Delay between those refetches. */
export const REFRESH_RETRY_DELAY = 1500;

/** Lock key for a reconcile that isn't waiting on any particular platform. */
const ANY_PLATFORM = '__any__';

/** Reconciles currently running, keyed by the platform each is waiting for. */
const inFlight = new Set<string>();

const sleep = (ms: number) => new Promise(resolve => setTimeout(resolve, ms));

export interface RefreshOptions {
  maxAttempts?: number;
  retryDelay?: number;
}

/**
 * Refetch the connection list until `target` shows up as active, or the attempt
 * budget runs out.
 *
 * One refetch isn't enough: the provider's redirect regularly beats its own
 * webhook, so the list still comes back without the account the user just
 * authorized and the platform reads as disconnected until a manual refresh.
 *
 * `target` is a frontend platform key, or null to reconcile once without
 * waiting for anything in particular (the socket event names no platform).
 */
export async function refreshUntilConnected(
  target: string | null,
  { maxAttempts = REFRESH_MAX_ATTEMPTS, retryDelay = REFRESH_RETRY_DELAY }: RefreshOptions = {}
): Promise<void> {
  // Dedupe concurrent triggers for the same platform; the loop retries anyway.
  const lock = target ?? ANY_PLATFORM;
  if (inFlight.has(lock)) return;
  inFlight.add(lock);

  try {
    for (let attempt = 0; attempt < maxAttempts; attempt++) {
      // Always a background reconciliation — the page already did its own
      // non-silent load, so this must never flash the full-screen loader.
      await useIntegrationsStore.getState().fetchConnections({ silent: true });
      if (!target) return;

      const connected = useIntegrationsStore
        .getState()
        .connections.some(c => toFrontendKey(c.platform) === target && c.status === 'active');
      if (connected) return;

      if (attempt < maxAttempts - 1) await sleep(retryDelay);
    }
  } finally {
    inFlight.delete(lock);
  }
}
